/**
 * PHASE 2 STANDARDIZATION - Standardized QuickView Integration Hook
 * 
 * Simplified hook that provides consistent QuickView integration
 * for any component. Replaces manual QuickView state management.
 * 
 * Usage:
 * const { handleQuickView } = useStandardQuickView();
 * <button onClick={() => handleQuickView(product)}>Quick View</button>
 */

import { useCallback } from 'react';
import { useQuickViewContext } from '@/contexts/QuickViewContext';
import { DisplayProduct, createDisplayProduct, isDisplayProduct } from '@/types/productDisplay';
import { productionLogger } from '@/utils/production-logger';

export interface StandardQuickViewReturn {
  handleQuickView: (product: any) => void;
  isQuickViewOpen: boolean;
  quickViewProduct: DisplayProduct | null;
}

/**
 * Standardized QuickView integration hook
 * Automatically converts any product format to DisplayProduct
 */
export const useStandardQuickView = (): StandardQuickViewReturn => {
  const { quickViewProduct, isQuickViewOpen, openQuickView } = useQuickViewContext();

  const handleQuickView = useCallback((product: any) => {
    if (!product) {
      productionLogger.warn('useStandardQuickView: Attempted to open QuickView with null/undefined product');
      return;
    }

    try {
      // Convert to DisplayProduct format for consistent handling
      const displayProduct: DisplayProduct = isDisplayProduct(product) 
        ? product 
        : createDisplayProduct(product);
      
      openQuickView(displayProduct);
      productionLogger.debug('Standard QuickView opened for product:', displayProduct.title);
    } catch (error) {
      productionLogger.error('Failed to open standard QuickView:', error);
    }
  }, [openQuickView]);

  return {
    handleQuickView,
    isQuickViewOpen,
    quickViewProduct
  };
};

export default useStandardQuickView;